import {Component, inject} from '@angular/core';
import {BaseError} from '../../models/error/BaseError';
import {ToastService} from '../../services/userInterface/toast.service';
import {IonButton, IonCol, IonContent, IonGrid, IonModal, IonRow, IonSkeletonText} from '@ionic/angular/standalone';
import {forkJoin, Observable, of, Subscription, tap} from 'rxjs';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LoadingSpinnerService} from '../../services/userInterface/loading-spinner.service';
import {ErrorManagerService} from '../../services/error/error-manager.service';
import {MatPaginatorIntl, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {CustomPaginatorIntl} from '../../models/materialComponent/CustomPaginatorIntl';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {TimePeriod} from '../../models/TimePeriod';
import {DateRangePickerComponent} from '../common/form/date-range-picker/date-range-picker.component';
import {TenantService} from '../../services/tenant/tenant.service';
import {TenantApplication} from '../../models/tenant/TenantApplication';
import {SearchableSelectComponent} from '../common/form/searchable-select/searchable-select.component';
import {CacheService} from '../../services/cache/cache.service';
import {
    ErrorCardSkeletonLoadingComponent
} from '../common/error-card-skeleton-loading/error-card-skeleton-loading.component';
import {ErrorSummaryCardComponent} from '../error-summary-card/error-summary-card.component';
import {LoggableEntity} from '../../models/error/LoggableEntity';
import {LogBookSummaryCardComponent} from '../log-book-summary-card/log-book-summary-card.component';
import {CommonModule} from '@angular/common';
import {HeaderComponent} from '../common/header/header.component';
import {ErrorHubService} from '../../services/websocket/error-hub.service';

@Component({
    selector: 'app-error-overview',
    standalone: true,
    imports: [CommonModule, ErrorCardSkeletonLoadingComponent, IonSkeletonText, IonContent, IonButton, IonGrid, IonCol, IonRow, IonModal, TranslatePipe, MatPaginatorModule, ReactiveFormsModule, DateRangePickerComponent, SearchableSelectComponent, HeaderComponent],
    templateUrl: './error-overview.component.html',
    styleUrl: './error-overview.component.css',
    providers: [{provide: MatPaginatorIntl, useClass: CustomPaginatorIntl}],
})
export class ErrorOverviewComponent {
    private static CACHE_EXPIRATION_TIME_IN_MINUTES: number = 24 * 60;
    private static CACHE_KEY_ERROR_FILTER: string = "currentErrorFilter";

    private errorManager: ErrorManagerService = inject(ErrorManagerService);
    private tenantService: TenantService = inject(TenantService);
    private toastService: ToastService = inject(ToastService);
    private translateService: TranslateService = inject(TranslateService);
    private errorHubService: ErrorHubService = inject(ErrorHubService);
    private loadingSpinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);
    private cacheService: CacheService<ErrorFilter> = inject(CacheService);

    public errorItems: LoggableEntity[] = [];
    public applications: TenantApplication[] = [];
    public isLoadingPage: boolean = true;
    public isLoadingErrors: boolean = false;
    private newErrorSubscription?: Subscription;

    public currentPageIndex: number = 0;
    public pageSize: number = 6;
    public totalAmountOfErrorItems: number = 0;

    public maxPeriodDifference: number = 14;
    public maxSelectedApplications: number = 10;
    public warningTresholdApplications: number = 2;
    public currentErrorFilter!: ErrorFilter;

    public filterForm = new FormGroup({
        applications: new FormControl<TenantApplication[]>([], {nonNullable: true}),
        timePeriod: new FormGroup({
            startDate: new FormControl<string>(new Date().toISOString(), {nonNullable: true}),
            endDate: new FormControl<string>(new Date().toISOString(), {nonNullable: true})
        })
    });

    public get totalAmountOfSelectedApplications() {
        return this.filterForm.getRawValue().applications.length;
    }

    async ionViewWillEnter() {
        this.initializeFilter();
        await this.initializeHubConnection();
        await this.refreshErrorList();
        await this.loadFullPage(this.currentPageIndex);
    }

    ionViewWillLeave() {
        this.newErrorSubscription?.unsubscribe();
    }

    private async initializeHubConnection() {
        await this.errorHubService.initializeHubConnection();
    }

    public async filter() {
        this.resetPaginator();
        this.currentErrorFilter = this.createErrorFilter();
        this.cacheService.saveData(this.currentErrorFilter, ErrorOverviewComponent.CACHE_KEY_ERROR_FILTER, ErrorOverviewComponent.CACHE_EXPIRATION_TIME_IN_MINUTES);
        await this.retrieveAllErrors(this.currentPageIndex);
    }

    public async changePage(pageEvent: PageEvent) {
        this.currentPageIndex = pageEvent.pageIndex;
        await this.retrieveAllErrors(this.currentPageIndex);
    }

    public compareApplications(firstApplication: TenantApplication, secondApplication: TenantApplication): boolean {
        return firstApplication.id === secondApplication.id;
    }

    public clearFilter() {
        this.filterForm.patchValue({
            applications: [],
            timePeriod: {
                startDate: new Date().toISOString(),
                endDate: new Date().toISOString()
            }
        });
        this.filter();
    }

    public getSummaryCard(loggableEntity: LoggableEntity) {
        return loggableEntity instanceof BaseError ? ErrorSummaryCardComponent : LogBookSummaryCardComponent;
    }

    public createInputs(loggableEntity: LoggableEntity) {
        return {
            "loggableEntity": loggableEntity
        }
    }

    private initializeFilter() {
        const cachedFilter = this.cacheService.getData(ErrorOverviewComponent.CACHE_KEY_ERROR_FILTER);
        if (cachedFilter) {
            cachedFilter.occurredTimeRange = new TimePeriod(
                new Date(cachedFilter.occurredTimeRange.from),
                new Date(cachedFilter.occurredTimeRange.to)
            );

            this.currentErrorFilter = cachedFilter;
            this.filterForm.patchValue({
                applications: this.currentErrorFilter.applications,
                timePeriod: {
                    startDate: this.currentErrorFilter.occurredTimeRange.from.toISOString(),
                    endDate: this.currentErrorFilter.occurredTimeRange.to.toISOString(),
                }
            });
        } else {
            this.currentErrorFilter = this.createErrorFilter();
        }
    }

    private resetPaginator() {
        this.currentPageIndex = 0;
        this.totalAmountOfErrorItems = 0;
    }

    private async refreshErrorList() {
        this.newErrorSubscription = this.errorHubService.newErrorIsRegistered().subscribe({
            next: async () => {
                await this.toastService.showSuccessMessage(this.translateService.instant("toastMessage.success.refreshMessages"));
                await this.retrieveAllErrors(this.currentPageIndex);
            },
            error: async (error) => {
                await this.toastService.showErrorMessage(this.translateService.instant("toastMessage.error.refreshMessages"))
            }
        });
    }

    private async loadFullPage(pageNumber: number) {
        this.isLoadingPage = true;
        await this.loadingSpinnerService.showSpinner();
        this.getAllApplicationsForFilter().subscribe({
            next: async () => {
                this.isLoadingPage = false;
                this.isLoadingErrors = true;
                await this.loadingSpinnerService.hideSpinner();
                await this.retrieveAllErrors(pageNumber);
            },
            error: async (error) => {
                await this.toastService.showErrorMessage();
                this.isLoadingPage = false;
                await this.loadingSpinnerService.hideSpinner();
            },
        });
    }

    private async retrieveAllErrors(pageNumber: number) {
        this.isLoadingErrors = true;
        forkJoin({
            errorItems: this.errorManager.getPaginatedErrors(pageNumber, this.pageSize, this.currentErrorFilter),
            totalAmountOfErrorItems: this.errorManager.getTotalAmountOfErrorItems(this.currentErrorFilter)
        }).subscribe({
            next: async ({errorItems, totalAmountOfErrorItems}) => {
                this.errorItems = errorItems;
                this.totalAmountOfErrorItems = totalAmountOfErrorItems;
                this.isLoadingErrors = false;
            },
            error: async (error) => {
                await this.toastService.showErrorMessage();
                this.isLoadingErrors = false;
            },
        });
    }

    private getAllApplicationsForFilter(): Observable<TenantApplication[]> {
        return this.applications.length > 0 ? of(this.applications) : this.tenantService.getAllApplicationsForCurrentUser().pipe(
            tap((applications: TenantApplication[]) => {
                this.applications = applications;
            })
        )
    }

    private createErrorFilter(): ErrorFilter {
        const formValues = this.filterForm.getRawValue();
        const selectedApplications = formValues.applications;
        return new ErrorFilter(
            selectedApplications.length < this.applications.length ? selectedApplications : [],
            new TimePeriod(
                new Date(new Date(formValues.timePeriod.startDate).setUTCHours(0, 0, 0, 0)),
                new Date(new Date(formValues.timePeriod.endDate).setUTCHours(23, 59, 59, 999))
            ),
        );
    }
}
